/**
 * WHAT THE FATE — Core Shopify Storefront Interactions
 * Dark Luxury Streetwear Architecture
 */

// Helper to format currency
function formatMoney(cents) {
  if (typeof cents !== 'number') cents = 0;
  const inRupees = Math.round(cents / 100);
  return '₹' + inRupees.toLocaleString('en-IN');
}

function escapeHTML(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function shopRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

// Product JSON cache (used by the quick-add sheet and bag size dropdowns)
const WTF_productCache = {};
async function WTF_getProduct(handle) {
  if (!handle) return null;
  if (!WTF_productCache[handle]) {
    WTF_productCache[handle] = fetch(shopRoot() + 'products/' + encodeURIComponent(handle) + '.js')
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }
  return WTF_productCache[handle];
}

function optionName(option) {
  return typeof option === 'string' ? option : option && option.name ? option.name : '';
}

// Index of the product's size option, or -1 when there isn't one
function WTF_sizeIndex(product) {
  if (!product || !Array.isArray(product.options)) return -1;
  const idx = product.options.findIndex((o) => optionName(o).toLowerCase().includes('size'));
  if (idx !== -1) return idx;
  return product.options.length === 1 ? 0 : -1;
}

// Swap a bag line to another variant, keeping quantity and line properties
window.WTF_swapLine = async function ({ key, variantId, quantity, properties, sellingPlan }) {
  const addBody = { items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] };
  if (properties && typeof properties === 'object' && Object.keys(properties).length) {
    addBody.items[0].properties = properties;
  }
  if (sellingPlan) addBody.items[0].selling_plan = Number(sellingPlan);

  const addRes = await fetch(shopRoot() + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addBody)
  });
  if (!addRes.ok) throw new Error('add failed');

  const removeRes = await fetch(shopRoot() + 'cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  });
  if (!removeRes.ok) throw new Error('remove failed');
};

// Builds the size dropdown for a cart line rendered in JS (cart drawer)
function WTF_sizeSelectHTML(item, product, texts) {
  const sizeIdx = WTF_sizeIndex(product);
  if (sizeIdx === -1 || !product.variants) return '';
  const current = product.variants.find((v) => v.id === item.variant_id);
  if (!current) return '';
  const matches = product.variants.filter((v) =>
    v.options.every((opt, i) => i === sizeIdx || opt === current.options[i])
  );
  if (matches.length < 2) return '';

  const optionsHTML = matches
    .map((v) => {
      const selected = v.id === current.id ? ' selected' : '';
      const disabled = !v.available && v.id !== current.id ? ' disabled' : '';
      const label = escapeHTML(v.options[sizeIdx]) + (v.available ? '' : ' (' + escapeHTML(texts.soldOut) + ')');
      return `<option value="${v.id}" class="bg-[#101014]"${selected}${disabled}>${label}</option>`;
    })
    .join('');

  const props = item.properties && Object.keys(item.properties).length ? item.properties : {};
  const plan = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : '';

  return `
    <label class="flex items-center gap-2 mt-2 text-[11px] font-mono text-white/50 uppercase tracking-wider">
      <span>${escapeHTML(texts.size)}</span>
      <select
        class="cart-size-select bg-white/5 border border-white/20 rounded-full pl-3 pr-7 py-1 text-xs font-mono text-white uppercase focus:outline-none focus:ring-2 focus:ring-white/60 cursor-pointer disabled:opacity-50"
        data-line-key="${escapeHTML(item.key)}"
        data-quantity="${item.quantity}"
        data-properties="${escapeHTML(JSON.stringify(props))}"
        ${plan ? `data-selling-plan="${plan}"` : ''}
        aria-label="${escapeHTML(texts.size)}: ${escapeHTML(item.product_title)}"
      >${optionsHTML}</select>
    </label>
  `;
}

// "Add more items" suggestions in the bag
async function WTF_renderAddMore(wrapper, productId, excludeIds) {
  if (!wrapper) return;
  const list = wrapper.querySelector('.cart-add-more-list');
  if (!list) return;
  if (!productId) {
    wrapper.classList.add('hidden');
    return;
  }
  const limit = Number(wrapper.dataset.limit) || 4;
  const exclude = new Set((excludeIds || []).map(String));
  try {
    const url =
      shopRoot() + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + (limit + exclude.size);
    const res = await fetch(url);
    const data = res.ok ? await res.json() : { products: [] };
    const products = (data.products || [])
      .filter((p) => p.available && !exclude.has(String(p.id)))
      .slice(0, limit);

    if (!products.length) {
      wrapper.classList.add('hidden');
      return;
    }

    list.innerHTML = products
      .map((p) => {
        let img = p.featured_image || '';
        if (img.startsWith('//')) img = 'https:' + img;
        if (img) img += (img.includes('?') ? '&' : '?') + 'width=240';
        return `
          <div class="snap-start flex-shrink-0 w-28 sm:w-32">
            <div class="relative aspect-[3/4] rounded-md overflow-hidden bg-[#121214] border border-white/10">
              <a href="${escapeHTML(p.url)}" class="block w-full h-full">
                ${img ? `<img src="${escapeHTML(img)}" alt="${escapeHTML(p.title)}" width="240" height="320" loading="lazy" class="w-full h-full object-cover">` : ''}
              </a>
              <button type="button"
                class="quick-add-btn absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-white text-black hover:bg-white/90 shadow-xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                data-product-id="${p.id}"
                data-product-handle="${escapeHTML(p.handle)}"
                data-variant-id="${p.variants && p.variants[0] ? p.variants[0].id : ''}"
                aria-label="Quick Add: ${escapeHTML(p.title)}">
                <svg class="w-3.5 h-3.5 stroke-black stroke-[2.5] fill-none" viewBox="0 0 24 24" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
            </div>
            <p class="mt-1.5 text-[11px] text-white/85 truncate">${escapeHTML(p.title)}</p>
            <p class="text-[11px] font-mono text-white/60">${formatMoney(p.price)}</p>
          </div>
        `;
      })
      .join('');
    wrapper.classList.remove('hidden');
  } catch (err) {
    console.error('Add more items failed to load:', err);
    wrapper.classList.add('hidden');
  }
}

// Global Cart Drawer Sync Function
window.WTF_updateCart = async function () {
  const cartDrawer = document.getElementById('CartDrawer');
  const countBadges = document.querySelectorAll('.cart-count-badge');
  const drawerCount = document.getElementById('CartDrawerCount');
  const drawerSubtotal = document.getElementById('CartDrawerSubtotal');
  const drawerItems = document.getElementById('CartDrawerItems');

  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();

    // 1. Update count badges in header
    countBadges.forEach((b) => {
      b.textContent = cart.item_count;
      if (cart.item_count > 0) {
        b.classList.remove('hidden');
        b.style.display = 'flex';
      } else {
        b.classList.add('hidden');
        b.style.display = 'none';
      }
    });

    // 2. Update Drawer Header Count
    if (drawerCount) {
      drawerCount.textContent = cart.item_count;
    }

    // 3. Update Drawer Subtotal
    if (drawerSubtotal) {
      drawerSubtotal.textContent = formatMoney(cart.total_price);
    }

    // 3b. Refresh "Add more items" suggestions in the drawer
    const drawerAddMore = cartDrawer ? cartDrawer.querySelector('.cart-add-more') : null;
    if (drawerAddMore) {
      const first = cart.items[0];
      WTF_renderAddMore(
        drawerAddMore,
        first ? first.product_id : null,
        cart.items.map((i) => i.product_id)
      );
    }

    // 4. Update Drawer Items HTML
    if (drawerItems) {
      if (cart.items.length === 0) {
        drawerItems.innerHTML = `
          <div class="h-full flex flex-col items-center justify-center text-center p-6 text-white/50">
            <p class="text-sm font-medium uppercase tracking-wider text-white">Your bag is empty</p>
            <a href="/collections/all" class="mt-4 px-6 py-2.5 bg-white text-black text-xs font-bold uppercase tracking-widest rounded-full hover:scale-105 transition-all">Shop Latest Drop</a>
          </div>
        `;
      } else {
        const sizeTexts = {
          size: (cartDrawer && cartDrawer.dataset.textSize) || 'Size',
          soldOut: (cartDrawer && cartDrawer.dataset.textSoldOut) || 'Sold out'
        };
        const productsByHandle = {};
        await Promise.all(
          cart.items
            .filter((item) => !item.product_has_only_default_variant && item.handle)
            .map(async (item) => {
              productsByHandle[item.handle] = await WTF_getProduct(item.handle);
            })
        );

        drawerItems.innerHTML = cart.items
          .map((item) => {
            const sizeSelect = productsByHandle[item.handle]
              ? WTF_sizeSelectHTML(item, productsByHandle[item.handle], sizeTexts)
              : '';
            const imgUrl = item.image || (item.featured_image ? item.featured_image.url : '');
            return `
              <div class="pt-4 first:pt-0 flex gap-3.5 border-b border-white/5 pb-4 last:border-b-0" data-line-key="${item.key}">
                <img src="${imgUrl}" alt="${item.title}" class="w-20 h-24 object-cover rounded bg-[#121214] flex-shrink-0 border border-white/10" />
                <div class="flex-1 flex flex-col justify-between">
                  <div>
                    <div class="flex items-start justify-between gap-2">
                      <h3 class="text-xs font-medium text-white line-clamp-1 leading-snug">${item.product_title}</h3>
                      <button type="button" class="text-white/40 hover:text-red-400 remove-cart-item transition-colors p-1" data-line-key="${item.key}" aria-label="Remove piece">
                        <svg class="w-3.5 h-3.5 stroke-current stroke-2 fill-none" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                    </div>
                    ${item.variant_title ? `<p class="text-[11px] font-mono text-white/50 mt-0.5">${item.variant_title}</p>` : ''}
                    ${sizeSelect}
                  </div>
                  <div class="flex items-center justify-between mt-3">
                    <div class="flex items-center border border-white/20 rounded-sm bg-white/5">
                      <button type="button" class="cart-qty-btn px-2 py-0.5 text-xs text-white/60 hover:text-white transition-colors" data-line-key="${item.key}" data-qty="${item.quantity - 1}">-</button>
                      <span class="px-2 text-xs font-mono text-white">${item.quantity}</span>
                      <button type="button" class="cart-qty-btn px-2 py-0.5 text-xs text-white/60 hover:text-white transition-colors" data-line-key="${item.key}" data-qty="${item.quantity + 1}">+</button>
                    </div>
                    <span class="text-xs font-mono font-semibold text-white">${formatMoney(item.final_line_price)}</span>
                  </div>
                </div>
              </div>
            `;
          })
          .join('');

        // Attach remove listeners
        drawerItems.querySelectorAll('.remove-cart-item').forEach((btn) => {
          btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const key = btn.dataset.lineKey;
            if (!key) return;
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
              });
              await window.WTF_updateCart();
            } catch (err) {
              console.error('Failed to remove item:', err);
            }
          });
        });

        // Attach quantity buttons listeners
        drawerItems.querySelectorAll('.cart-qty-btn').forEach((btn) => {
          btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const key = btn.dataset.lineKey;
            const newQty = parseInt(btn.dataset.qty, 10);
            if (!key || isNaN(newQty)) return;
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: newQty })
              });
              await window.WTF_updateCart();
            } catch (err) {
              console.error('Failed to change quantity:', err);
            }
          });
        });
      }
    }
  } catch (err) {
    console.error('Cart refresh failed:', err);
  }
};

document.addEventListener('DOMContentLoaded', () => {
  // Sticky header scroll behavior
  const header = document.getElementById('SiteHeader');
  if (header) {
    window.addEventListener(
      'scroll',
      () => {
        if (window.scrollY > 40) {
          header.classList.add('glass-nav');
        } else {
          header.classList.remove('glass-nav');
        }
      },
      { passive: true }
    );
  }

  // Cart Drawer Open/Close
  const cartDrawer = document.getElementById('CartDrawer');
  const openCartBtns = document.querySelectorAll('[data-action="open-cart"]');
  const closeCartBtns = document.querySelectorAll('[data-action="close-cart"]');

  openCartBtns.forEach((btn) =>
    btn.addEventListener('click', () => {
      if (cartDrawer) {
        cartDrawer.classList.remove('hidden');
        window.WTF_updateCart();
      }
    })
  );

  closeCartBtns.forEach((btn) =>
    btn.addEventListener('click', () => {
      if (cartDrawer) cartDrawer.classList.add('hidden');
    })
  );

  // Search Modal
  const searchModal = document.getElementById('SearchModal');
  const openSearchBtns = document.querySelectorAll('[data-action="open-search"]');
  const closeSearchBtns = document.querySelectorAll('[data-action="close-search"]');

  openSearchBtns.forEach((btn) =>
    btn.addEventListener('click', () => {
      if (searchModal) {
        searchModal.classList.remove('hidden');
        const input = document.getElementById('SearchModalInput');
        if (input) input.focus();
      }
    })
  );

  closeSearchBtns.forEach((btn) =>
    btn.addEventListener('click', () => {
      if (searchModal) searchModal.classList.add('hidden');
    })
  );

  // ESC to close all modals
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (cartDrawer) cartDrawer.classList.add('hidden');
      if (searchModal) searchModal.classList.add('hidden');
      if (window.WTF_closeQuickAdd) window.WTF_closeQuickAdd();
    }
  });

  // Wishlist Handling
  const WISHLIST_KEY = 'wtf_shopify_wishlist';
  const WISHLIST_HANDLES_KEY = 'wtf_shopify_wishlist_handles';

  const getWishlist = () => {
    try {
      const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(list) ? list.map(String) : [];
    } catch {
      return [];
    }
  };

  const getWishlistHandles = () => {
    try {
      const map = JSON.parse(localStorage.getItem(WISHLIST_HANDLES_KEY) || '{}');
      return map && typeof map === 'object' ? map : {};
    } catch {
      return {};
    }
  };

  const saveWishlist = (list, handles) => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    localStorage.setItem(WISHLIST_HANDLES_KEY, JSON.stringify(handles));
  };

  const updateWishlistUI = () => {
    const list = getWishlist();
    const handles = getWishlistHandles();
    let handlesChanged = false;

    const badges = document.querySelectorAll('.wishlist-count-badge');
    badges.forEach((b) => {
      b.textContent = list.length;
      if (list.length > 0) {
        b.classList.remove('hidden');
        b.style.display = 'flex';
      } else {
        b.classList.add('hidden');
        b.style.display = 'none';
      }
    });

    document.querySelectorAll('.wishlist-btn').forEach((btn) => {
      const pId = btn.dataset.productId;
      const saved = list.includes(pId);
      // Backfill handles for items saved before handles were stored
      if (saved && !handles[pId] && btn.dataset.productHandle) {
        handles[pId] = btn.dataset.productHandle;
        handlesChanged = true;
      }
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      if (saved) {
        btn.classList.add('bg-white', 'text-black');
        btn.classList.remove('bg-black/40', 'bg-black/50', 'text-white/80');
        const svg = btn.querySelector('svg');
        if (svg) svg.classList.add('fill-black');
      } else {
        btn.classList.remove('bg-white', 'text-black');
        btn.classList.add('bg-black/40', 'text-white/80');
        const svg = btn.querySelector('svg');
        if (svg) svg.classList.remove('fill-black');
      }
    });

    if (handlesChanged) saveWishlist(list, handles);
  };

  window.WTF_getWishlist = () => {
    const handles = getWishlistHandles();
    return getWishlist().map((id) => ({ id, handle: handles[id] || null }));
  };
  window.WTF_updateWishlistUI = updateWishlistUI;

  updateWishlistUI();

  // Delegated so cards added later (e.g. on the wishlist page) also work
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const pId = btn.dataset.productId;
    if (!pId) return;

    let list = getWishlist();
    const handles = getWishlistHandles();
    let added;
    if (list.includes(pId)) {
      list = list.filter((id) => id !== pId);
      delete handles[pId];
      added = false;
    } else {
      list.push(pId);
      if (btn.dataset.productHandle) handles[pId] = btn.dataset.productHandle;
      added = true;
    }
    saveWishlist(list, handles);
    updateWishlistUI();
    document.dispatchEvent(new CustomEvent('wtf:wishlist-change', { detail: { productId: pId, added } }));
  });

  document.querySelectorAll('[data-action="open-wishlist"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      location.href = root + 'collections/all?view=wishlist';
    });
  });

  // Size change inside the bag (cart page + cart drawer)
  document.addEventListener('change', async (e) => {
    const select = e.target.closest('.cart-size-select');
    if (!select) return;
    const previous = Array.from(select.options).find((o) => o.defaultSelected);
    let properties = {};
    try {
      properties = JSON.parse(select.dataset.properties || '{}') || {};
    } catch {
      properties = {};
    }
    select.disabled = true;
    try {
      await window.WTF_swapLine({
        key: select.dataset.lineKey,
        variantId: select.value,
        quantity: select.dataset.quantity,
        properties,
        sellingPlan: select.dataset.sellingPlan
      });
      if (select.closest('#CartDrawer')) {
        await window.WTF_updateCart();
      } else {
        location.reload();
      }
    } catch (err) {
      console.error('Size change failed:', err);
      if (previous) select.value = previous.value;
      select.disabled = false;
      const msg = (cartDrawer && cartDrawer.dataset.textError) || 'Sorry, that size could not be added. Please try another.';
      alert(msg);
    }
  });

  // Quick Add: half-screen size picker
  const quickAddModal = document.getElementById('QuickAddModal');
  const quickAddContent = document.getElementById('QuickAddModalContent');
  let quickAddTrigger = null;

  const qaText = (key, fallback) => (quickAddModal && quickAddModal.dataset[key]) || fallback;

  const openQuickAdd = () => {
    if (!quickAddModal) return;
    quickAddModal.classList.remove('hidden');
    quickAddModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const sheet = quickAddModal.querySelector('.quick-add-sheet');
    requestAnimationFrame(() => sheet && sheet.classList.remove('translate-y-full'));
  };

  const closeQuickAdd = () => {
    if (!quickAddModal || quickAddModal.classList.contains('hidden')) return;
    const sheet = quickAddModal.querySelector('.quick-add-sheet');
    if (sheet) sheet.classList.add('translate-y-full');
    quickAddModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(() => quickAddModal.classList.add('hidden'), 250);
    if (quickAddTrigger) quickAddTrigger.focus();
  };
  window.WTF_closeQuickAdd = closeQuickAdd;

  if (quickAddModal) {
    quickAddModal.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="close-quick-add"]')) closeQuickAdd();
    });
  }

  const addVariantToBag = async (variantId) => {
    const res = await fetch(shopRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    });
    if (!res.ok) throw new Error('add failed');
    // On the full bag page, reload so the new item shows in the list and totals
    if (document.getElementById('MainCartForm')) {
      location.reload();
      return;
    }
    await window.WTF_updateCart();
    if (cartDrawer) cartDrawer.classList.remove('hidden');
  };

  // Load "Add more items" on the full bag page
  document.querySelectorAll('#MainCartForm .cart-add-more').forEach((wrapper) => {
    WTF_renderAddMore(
      wrapper,
      wrapper.dataset.productId,
      (wrapper.dataset.exclude || '').split(',').filter(Boolean)
    );
  });

  const renderQuickAdd = (product) => {
    const options = (product.options || []).map((o, i) => ({
      name: optionName(o),
      values: (o && o.values) || Array.from(new Set(product.variants.map((v) => v.options[i])))
    }));
    const sizeIdx = WTF_sizeIndex(product);
    const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
    // Preselect everything except size, so the buyer is asked to pick a size
    const selected = options.map((_, i) => (i === sizeIdx ? null : firstAvailable.options[i]));

    const findVariant = () =>
      selected.every((v) => v !== null)
        ? product.variants.find((v) => v.options.every((opt, i) => opt === selected[i]))
        : null;

    const valueAvailable = (optIdx, value) =>
      product.variants.some(
        (v) =>
          v.available &&
          v.options[optIdx] === value &&
          v.options.every((opt, i) => i === optIdx || selected[i] === null || opt === selected[i])
      );

    const image = product.featured_image
      ? (product.featured_image.startsWith('//') ? 'https:' : '') + product.featured_image
      : '';

    const draw = () => {
      const variant = findVariant();
      const price = variant ? variant.price : product.price;
      const canAdd = variant && variant.available;
      let buttonLabel = qaText('textAdd', 'Add to bag');
      if (!variant) buttonLabel = qaText('textSelectSize', 'Select a size');
      else if (!variant.available) buttonLabel = qaText('textSoldOut', 'Sold out');

      quickAddContent.innerHTML = `
        <div class="flex gap-4 items-start pr-8">
          ${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(product.title)}" class="w-20 h-24 object-cover rounded-md bg-[#121214] border border-white/10 flex-shrink-0">` : ''}
          <div class="min-w-0">
            <h2 id="QuickAddTitle" class="text-sm font-semibold text-white leading-snug">${escapeHTML(product.title)}</h2>
            <p class="text-sm font-mono font-bold text-white mt-1">${formatMoney(price)}</p>
          </div>
        </div>
        ${options
          .map(
            (opt, optIdx) => `
          <fieldset class="mt-5">
            <legend class="text-[11px] font-mono uppercase tracking-widest text-white/60 mb-2">${escapeHTML(opt.name)}</legend>
            <div class="flex flex-wrap gap-2">
              ${opt.values
                .map((value) => {
                  const isSelected = selected[optIdx] === value;
                  const available = valueAvailable(optIdx, value);
                  return `<button type="button"
                    class="qa-option min-w-[3rem] px-3.5 py-2 rounded-full border text-xs font-mono uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                      isSelected
                        ? 'bg-white text-black border-white'
                        : available
                          ? 'border-white/25 text-white hover:border-white'
                          : 'border-white/10 text-white/30 line-through'
                    }"
                    data-option-index="${optIdx}"
                    data-value="${escapeHTML(value)}"
                    aria-pressed="${isSelected}"
                    ${available ? '' : `aria-label="${escapeHTML(value)} (${escapeHTML(qaText('textSoldOut', 'Sold out'))})"`}
                  >${escapeHTML(value)}</button>`;
                })
                .join('')}
            </div>
          </fieldset>`
          )
          .join('')}
        <p class="qa-error hidden mt-4 text-xs text-red-400" role="alert"></p>
        <button type="button" class="qa-add mt-6 w-full py-3.5 bg-white text-black hover:bg-white/90 text-xs font-bold uppercase tracking-[0.2em] rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed" ${canAdd ? '' : 'disabled'}>
          ${escapeHTML(buttonLabel)}
        </button>
      `;

      quickAddContent.querySelectorAll('.qa-option').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = Number(btn.dataset.optionIndex);
          selected[idx] = btn.dataset.value;
          draw();
          const again = quickAddContent.querySelector(
            `.qa-option[data-option-index="${idx}"][data-value="${CSS.escape(btn.dataset.value)}"]`
          );
          if (again) again.focus();
        });
      });

      const addBtn = quickAddContent.querySelector('.qa-add');
      addBtn.addEventListener('click', async () => {
        const v = findVariant();
        if (!v || !v.available) return;
        addBtn.disabled = true;
        addBtn.textContent = qaText('textAdding', 'Adding...');
        try {
          await addVariantToBag(v.id);
          closeQuickAdd();
        } catch (err) {
          console.error('Quick add failed:', err);
          const errEl = quickAddContent.querySelector('.qa-error');
          errEl.textContent = qaText('textError', 'Sorry, that size could not be added. Please try another.');
          errEl.classList.remove('hidden');
          addBtn.disabled = false;
          addBtn.textContent = qaText('textAdd', 'Add to bag');
        }
      });
    };

    draw();
    const firstOption = quickAddContent.querySelector('.qa-option');
    if (firstOption) firstOption.focus();
  };

  // Quick Add Button Handlers (delegated so dynamically added cards work too)
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.quick-add-btn');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.productHandle;
    const fallbackVariant = btn.dataset.variantId;
    quickAddTrigger = btn;

    // No sheet or handle: fall back to adding the default variant
    if (!quickAddModal || !quickAddContent || !handle) {
      if (fallbackVariant) addVariantToBag(fallbackVariant).catch((err) => console.error('Quick add failed:', err));
      return;
    }

    quickAddContent.innerHTML = `<p class="text-xs font-mono text-white/60 text-center py-8 uppercase tracking-widest">${escapeHTML(qaText('textLoading', 'Loading sizes...'))}</p>`;
    openQuickAdd();

    const product = await WTF_getProduct(handle);
    if (!product || !product.variants) {
      closeQuickAdd();
      return;
    }

    // Single-variant products go straight to the bag
    if (product.variants.length === 1) {
      closeQuickAdd();
      if (product.variants[0].available) {
        addVariantToBag(product.variants[0].id).catch((err) => console.error('Quick add failed:', err));
      }
      return;
    }

    renderQuickAdd(product);
  });

  // Initial cart count sync on load
  window.WTF_updateCart();
});
